import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import { MafaDrawer } from './MafaDrawer';

type Props = {
  opened: boolean;
  onClose: () => void;
};

type Topic = { icon: IconName; title: string; subtitle: string };
type Shortcut = { keys: string; description: string };

const TOPICS: Topic[] = [
  {
    icon: 'folder',
    title: 'Consulter un dossier',
    subtitle: 'Rechercher un entreprenant et lire sa timeline 360°.',
  },
  {
    icon: 'upload',
    title: 'Charger un fichier',
    subtitle: "Assistant d'import en 4 étapes avec simulation à blanc.",
  },
  {
    icon: 'key',
    title: 'Émettre une clé API',
    subtitle: 'Clé visible une seule fois, stockée hachée côté serveur.',
  },
  {
    icon: 'inbox',
    title: 'Rejouer un rejet',
    subtitle: 'Depuis la file DLQ, verbatim ou après correction.',
  },
];

const SHORTCUTS: Shortcut[] = [
  { keys: '⌘ K', description: 'Recherche globale (entreprenants, imports, clés)' },
  { keys: 'G puis D', description: 'Aller aux Dossiers' },
  { keys: 'G puis I', description: 'Aller aux Imports' },
  { keys: 'G puis P', description: 'Aller aux Partenaires' },
  { keys: 'G puis A', description: "Aller à l'Administration" },
  { keys: 'Échap', description: 'Fermer un panneau ou un modal' },
];

/**
 * Help drawer rendered with our own portal-based MafaDrawer so we don't
 * have to fight Mantine's cascade for header alignment and body padding.
 */
export function HelpDrawer({ opened, onClose }: Props) {
  const { t } = useTranslation();

  return (
    <MafaDrawer
      opened={opened}
      onClose={onClose}
      title={t('help.title')}
      subtitle={t('help.subtitle') ?? undefined}
      icon="help"
      size={520}
      footer={
        <>
          <Button variant="primary" icon="link">
            Documentation complète
          </Button>
          <Button variant="ghost" style={{ marginLeft: 'auto' }}>
            support@pde.ci
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        <section>
          <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 12, color: 'var(--ink)' }}>
            Tâches courantes
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {TOPICS.map((tp) => (
              <div
                key={tp.title}
                style={{
                  display: 'flex',
                  gap: 13,
                  padding: 14,
                  borderRadius: 12,
                  border: '1px solid var(--line)',
                  background: 'var(--panel)',
                }}
              >
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    background: 'var(--panel-2)',
                    border: '1px solid var(--line)',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'var(--accent-2)',
                    flex: '0 0 auto',
                  }}
                >
                  <Icon name={tp.icon} size={19} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--ink)' }}>{tp.title}</div>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      marginTop: 3,
                      color: 'var(--ink-3)',
                      lineHeight: 1.45,
                    }}
                  >
                    {tp.subtitle}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 12, color: 'var(--ink)' }}>
            Raccourcis clavier
          </div>
          <div
            style={{
              background: 'var(--panel)',
              border: '1px solid var(--line)',
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            {SHORTCUTS.map((sc, i) => (
              <div
                key={sc.keys}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderBottom: i < SHORTCUTS.length - 1 ? '1px solid var(--line)' : 'none',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
                  {sc.description}
                </span>
                <span
                  style={{
                    font: '700 11px/1 var(--mono)',
                    color: 'var(--ink-3)',
                    background: 'var(--panel-2)',
                    border: '1px solid var(--line)',
                    borderRadius: 6,
                    padding: '5px 8px',
                  }}
                >
                  {sc.keys}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </MafaDrawer>
  );
}
